/*
 * $Id:: index.js 2021/03/02 11:11 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import GridIconSelected from '../../assets/images/gridIcon.svg';
import GridIcon from '../../assets/images/gridIconSelected.svg';
import ListIconSelected from '../../assets/images/listIcon.svg';
import ListIcon from '../../assets/images/listIconSelected.svg';
import { ReactComponent as RefreshIcon } from '../../assets/images/refresh_icon.svg';
import ErrorHandler from '../../common/components/ErrorHandler';
import { userCan } from '../../common/utils/service';
import ApplicationsGrid from '../../components/ApplicationsGrid';
import ApplicationsTable from '../../components/ApplicationsTable';
import Card from '../../components/Card';
import CustomPagination from '../../components/CustomPagination';
import ApplicationFilters from '../../components/Filters/ApplicationFilters';
import PageTitle from '../../components/PageTitle';
import SmallPagination from '../../components/SmallPagination';
import WarningModal from '../../components/WarningModal';
import { t } from '../../providers';
import { me as meSlice } from '../AuthenticatedPage/authenticationSlice';
import { SLICE_NAME } from './constants';
import filterItems from './filterItems';
import { list, loading as loadingSlice, error as errorSlice, exportLoading as exportLoadingSlice } from './finalizedApplicationsSlice';
import { applicationsParserGrid, applicationsParserList, filtersParser, myApplicationsExportColumns } from './utils';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles((_theme) => ({
    titleAndSwitch: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '@media (max-width:1100px)': {
            display: 'block',
        },
    },
    switch: {
        display: 'flex',
        '@media (max-width:1100px)': {
            float: 'right',
            marginRight: '70px',
        },
        '@media (max-width:940px)': {
            float: 'right',
            marginRight: '0px',
        },
        '@media (max-width:600px)': {
            float: 'right',
            marginRight: '0px',
            marginTop: '-28px',
        },
    },
    switchGrid: {
        '&.disabled': {
            cursor: 'not-allowed',
            '& .MuiButtonBase-root': {
                cursor: 'not-allowed',
            },
        },
        '& .MuiFormControlLabel-label': {
            color: '#272833',
            fontWeight: '600',
            fontSize: '0.875rem',
            lineHeight: '29px',
            paddingLeft: '8px',
        },
    },
    tabs: {
        '& .MuiTabs-scroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'unset',
        },
        '@media (max-width:600px)': {
            marginTop: '25px',
        },
        '& .MuiTab-root': {
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
            minWidth: '100px',
            '@media (max-width:600px)': {
                minWidth: '120px',
            },
            '@media (max-width:400px)': {
                minWidth: '70px',
            },
        },
    },
    tabContainer: {
        width: '100%',
    },
    filtersGrid: {
        marginTop: '30px',
    },
    cardTable: {
        '@media (max-width:960px)': {
            marginRight: 'unset',
        },
    },
    listBtn: {
        borderColor: '#3F5E20',
        color: '#3F5E20',
        paddingLeft: '16px',
        paddingRight: '19.25px',
        borderRadius: '2px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
        '@media (max-width:600px)': {
            textIndent: '-9999px',
            paddingLeft: '7px',
        },
        '& img': {
            paddingLeft: 10,
        },
    },
    gridBtn: {
        '& img': {
            paddingLeft: 10,
        },
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
        borderColor: '#3F5E20',
        color: '#3F5E20',
        paddingLeft: '16px',
        paddingRight: '19.25px',
        borderRadius: '2px',
        '@media (max-width:600px)': {
            textIndent: '-9999px',
            paddingLeft: '7px',
        },
    },
    active: {
        background: '#D2E8BC !important',
        color: '#223211',
    },
    refreshButton: {
        color: '#3F5E20',
        fontSize: '0.75rem',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        textAlign: 'left',
        padding: '0px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
}));

const TRADEMARKS = 'trademark';
const DESIGNS = 'design';
const PATENTS = 'patents';
const ASC = 'ASC';
const DESC = 'DESC';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 0;
// const DEFAULT_SORT_COLUMN = 'applicationDate';
const DEFAULT_SORT_COLUMN = 'submissionDate';
const DEFAULT_SORT_TYPE = DESC;
const DEFAULT_SUBMIT_YEAR = new Date().getFullYear();

const MyApplicationsPage = () => {
    const classes = useStyles();
    const { service } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const loading = useSelector(loadingSlice);
    const exportLoading = useSelector(exportLoadingSlice);
    const error = useSelector(errorSlice);
    const applications = useSelector(list);
    const me = useSelector(meSlice);
    const isTablet = useMediaQuery('(max-width:960px)');

    const { content, totalElements, size, number } = applications || {};

    const [userType, setUserType] = useState('');
    const [sortProps, setSortProps] = useState({ sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE });
    const [gridDisplay, setGridDisplay] = useState(false);
    const [tabs, setTabs] = useState([]);
    const [selectedTab, setSelectedTab] = useState(!!~tabs.indexOf(service) ? tabs.indexOf(service) : 0); //index of tabs
    const [applicationsGrid, setApplicationsGrid] = useState([]);
    const [applicationsList, setApplicationsList] = useState([]);
    const [filtersValues, setFiltersValues] = useState({
        searchQuery: '',
        filters: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
        filtersChips: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
    });
    const [lastId, setLastId] = useState(0);
    const [requestBodyData, setRequestBodyData] = useState({
        paginationData: {
            page: DEFAULT_PAGE_NUMBER,
            size: DEFAULT_PAGE_SIZE,
        },
        sortingData: {
            sortColumn: sortProps.sortColumn,
            sortType: sortProps.sortType,
        },
        filteringData: filtersParser(filtersValues.filters),
        isDraft: false,
        isDashboard: false,
        lastId: lastId,
    });
    const [errorDialog, setErrorDialog] = useState({ visibility: false, message: '', title: '' });
    const [errorHandler, setErrorHandler] = useState({ hasRefresh: false, message: '', subtitle: '' });
    const [filterDialog, setFilterDialog] = useState({ visibility: false, message: '' });
    const [hideInfo, setHideInfo] = useState(false);
    const [exportError, setExportError] = useState(false);
    const [noDataToExport, setNoDataToExport] = useState(content && content.length === 0);
    const [tabChange, setTabChange] = useState(false);

    const getApplicationReceipt = (appId, ipRightType, requestStatus) => {
        if (requestStatus === 'request.status.cancelled') {
            setErrorDialog({ visibility: true, message: t('canceled.receipt.modal.message'), title: '' });
        } else {
            dispatch({ type: `${SLICE_NAME}/requestApplicationReceipt`, payload: { appId, ipRightType, applicationType: tabs[selectedTab] } });
        }
    };

    const viewProcessDetails = (registrationLink) => {
        navigateTo(registrationLink);
    };

    const navigateTo = (linkPath) => {
        if (!!linkPath) window.open(linkPath, '_blank');
    };

    const handleCloseDialog = () => {
        setErrorDialog({ visibility: false, message: '', title: '' });
        dispatch({ type: `${SLICE_NAME}/error`, payload: false });
    };

    const handleCloseFilterDialog = () => {
        setFilterDialog({ visibility: false, message: '' });
        setFiltersValues({ ...filtersValues, filters: {}, filtersChips: {} });
    };

    const handleDateFilter = (idx) => {
        const updatedDateFilter = filtersValues.filters.dates.filter((filter, id) => id !== idx);
        const updatedDateChips = filtersValues.filtersChips.dates.filter((filter, id) => id !== idx);
        let updatedFilter = { ...filtersValues.filters, dates: updatedDateFilter };
        let updatedChips = { ...filtersValues.filtersChips, dates: updatedDateChips };
        if (!updatedDateFilter.length) {
            delete updatedFilter.dates;
        }
        if (!updatedDateChips.length) {
            delete updatedChips.dates;
        }
        setFiltersValues({
            searchQuery: filtersValues.searchQuery,
            filters: updatedFilter,
            filtersChips: updatedChips,
        });
    };

    const handleCancelFilterDialog = () => {
        setFilterDialog({ visibility: false, message: '' });
    };

    const handleDisplayChange = (value) => {
        setGridDisplay(value);
    };

    const setSorting = (sortColumn) => {
        const isSameKey = sortColumn === sortProps.sortColumn;
        const oppositeSort = sortProps.sortType === ASC ? DESC : ASC;
        const sortType = !isSameKey ? ASC : oppositeSort;
        setSortProps({ sortColumn, sortType });
        setRequestBodyData({
            ...requestBodyData,
            paginationData: {
                page: DEFAULT_PAGE_NUMBER,
                size: size,
            },
            sortingData: {
                sortColumn: sortColumn,
                sortType: sortType,
            },
            lastId: lastId,
        });
    };

    const handleChangePage = (event, newPage) => {
        event.preventDefault();
        // setLastId(applications)
        if (newPage !== number) {
            setRequestBodyData({
                ...requestBodyData,
                paginationData: {
                    page: newPage,
                    size: size,
                },
                lastId: content[content.length - 1].applicationNumber,
            });
            setLastId(content[content.length - 1].applicationNumber);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const pageSize = event;
        if (size !== pageSize) {
            setRequestBodyData({
                ...requestBodyData,
                paginationData: {
                    page: 0,
                    size: pageSize,
                },
                lastId: content[content.length - 1].applicationNumber,
            });
            setLastId(content[content.length - 1].applicationNumber);
        }
    };

    const clearFilters = () => {
        clearFiltersWarning();
    };

    const clearFiltersWarning = () => {
        setFilterDialog({ visibility: true, message: 'nofilters.warning.text' });
    };

    const handleTabChange = (event, value) => {
        event.preventDefault();
        setTabChange(true);
        setHideInfo(false);
        setLastId(0);
        setFiltersValues({
            searchQuery: '',
            filters: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
            filtersChips: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
        });
        setSortProps({ sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE });
        setRequestBodyData({
            paginationData: {
                page: DEFAULT_PAGE_NUMBER,
                size: DEFAULT_PAGE_SIZE,
            },
            sortingData: {
                sortColumn: DEFAULT_SORT_COLUMN,
                sortType: DEFAULT_SORT_TYPE,
            },
            filteringData: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
            isDraft: false,
            isDashboard: false,
        });
        history.push(`/my-applications/${tabs[value]}`);
        // // if (tabs[value] === PATENTS) setGridDisplay(false); //only list available for e-services
        // // setSelectedTab(value);
    };

    const exportApplications = (type) => {
        setExportError(false);
        const exportedColumns = myApplicationsExportColumns(tabs[selectedTab]);

        let params = {
            requestBody: {
                ...requestBodyData,
                columns: type === 'excel' ? exportedColumns : [],
                isDraft: false,
                isDashboard: false,
                paginationData: {
                    page: DEFAULT_PAGE_NUMBER,
                    size: totalElements || 10,
                },
            },
            applicationType: tabs[selectedTab],
            type: type ? type : 'excel',
        };
        if (!params.requestBody.columns.length) delete params.requestBody.columns; // remove columns attribute if empty
        dispatch({ type: `${SLICE_NAME}/requestMyApplicationsExport`, payload: { params } });
    };

    const _initiateServices = (_service) => {
        // Do nothing for now.
    };

    const toggleInfoMessage = (value) => {
        setHideInfo(value);
    };

    useEffect(() => {
        const { authorities, personType } = me;
        setUserType(personType);
        const allowedTabs = [];
        if (userCan(authorities, 'ROLE_TRADEMARKS')) allowedTabs.push(TRADEMARKS);
        if (userCan(authorities, 'ROLE_DESIGNS')) allowedTabs.push(DESIGNS);
        if (userCan(authorities, 'ROLE_PATENTS')) allowedTabs.push(PATENTS);

        if (allowedTabs.length > 0) {
            setTabs(allowedTabs); //set tabs depending on tabs allowed based on authorities role
        } else {
            history.push('/');
        }
    }, [me, history]);

    useEffect(() => {
        if (!tabChange) {
            if (tabs.length > 0) {
                setSelectedTab(!!~tabs.indexOf(service) ? tabs.indexOf(service) : 0);
                setHideInfo(false);
                setLastId(0);
                setTabChange(true);
                setFiltersValues({
                    searchQuery: '',
                    filters: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
                    filtersChips: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
                });
                setSortProps({ sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE });
                setRequestBodyData({
                    paginationData: {
                        page: DEFAULT_PAGE_NUMBER,
                        size: DEFAULT_PAGE_SIZE,
                    },
                    sortingData: {
                        sortColumn: DEFAULT_SORT_COLUMN,
                        sortType: DEFAULT_SORT_TYPE,
                    },
                    filteringData: { submitYears: [`${DEFAULT_SUBMIT_YEAR}`] },
                    isDraft: false,
                    isDashboard: false,
                });
            }
        }
    }, [service]);

    const requestApplications = () => {
        setErrorHandler({ hasRefresh: false, message: '', subtitle: '' });
        setExportError(false);
        const params = {
            requestBody: requestBodyData,
            selectedTab: service || tabs[selectedTab],
            lastId: lastId,
        };
        dispatch({ type: `${SLICE_NAME}/requestMyApplications`, payload: { params } });
        setTabChange(false);
    };

    useEffect(() => {
        if (tabs.length > 0) {
            setSelectedTab(!!~tabs.indexOf(service) ? tabs.indexOf(service) : 0);
            requestApplications();
        }
    }, [dispatch, requestBodyData, tabs]);

    useEffect(() => {
        if (!tabChange) {
            if (filtersValues.searchQuery) {
                setRequestBodyData({
                    ...requestBodyData,
                    paginationData: { ...requestBodyData.paginationData, page: 0 },
                    filteringData: filtersParser(filtersValues.filters),
                    searchingData: filtersValues.searchQuery,
                });
            } else {
                const bodyData = { ...requestBodyData };
                delete bodyData['searchingData'];
                setRequestBodyData({
                    ...bodyData,
                    paginationData: { ...bodyData.paginationData, page: 0 },
                    filteringData: filtersParser(filtersValues.filters),
                });
            }
        }
    }, [filtersValues]);

    useEffect(() => {
        if (tabs.length > 0) {
            setNoDataToExport(content && content.length === 0);
            if (gridDisplay) {
                const appsGrid = applicationsParserGrid(applications || [], tabs[selectedTab], getApplicationReceipt, viewProcessDetails);
                setApplicationsGrid(appsGrid);
            } else {
                const appsList = applicationsParserList(
                    applications || [],
                    tabs[selectedTab],
                    userType,
                    getApplicationReceipt,
                    true,
                    viewProcessDetails
                );
                setApplicationsList(appsList);
            }
        }
    }, [applications, gridDisplay, userType]);

    useEffect(() => {
        if (error === 'Could not fetch application pdf') {
            setErrorDialog({ visibility: true, message: t('application.pdf.failed.fetching'), title: '' });
        } else if (error === 'Could not fetch false applications excel') {
            setExportError(true);
            setErrorDialog({ visibility: true, message: t('export.excel.unavailable'), title: t('export.error.title') });
        } else if (error === 'Could not fetch false applications pdf') {
            setExportError(true);
            setErrorDialog({ visibility: true, message: t('export.pdf.unavailable'), title: t('export.error.title') });
        } else if (error === 'export.pdf.maxLength') {
            setExportError(true);
            setErrorDialog({ visibility: true, message: t('export.pdf.maxLength'), title: t('export.error.title') });
        } else {
            if (error) {
                setNoDataToExport(true);
            }
            if (error && error !== null && error.extraInfo) {
                setErrorHandler({
                    message: error.message,
                    hasRefresh: error.extraInfo.hasRefresh || false,
                    subtitle: error.extraInfo.secondMessage || '',
                });
            }
        }
    }, [error]);

    const applicationTable = !loading ? (
        <ApplicationsTable
            setSorting={setSorting}
            sortBy={sortProps.sortColumn}
            sortType={sortProps.sortType}
            applications={applicationsList}
            tableId={'myApplications'}
            menuItems={[]}
            tableAriaLabel={t('my.applications.description')}
        />
    ) : (
        <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress aria-hidden="true" />
            <Typography variant="srOnly" role="alert">
                {t('loading.text')}
            </Typography>
        </Box>
    );

    const listBtnIcon = gridDisplay ? ListIcon : ListIconSelected;
    const gridBtnIcon = gridDisplay ? GridIconSelected : GridIcon;

    return (
        <div>
            {/* title and switch button */}
            <div className={classes.titleAndSwitch}>
                <div>
                    <PageTitle title={t('my.applications')} details={t('my.applications.description')} />
                </div>
                <div className={classes.switch}>
                    <ButtonGroup disableFocusRipple color="primary" aria-label={t('change.applications.between.views')}>
                        <Button
                            aria-pressed={!gridDisplay}
                            onClick={() => {
                                handleDisplayChange(false);
                            }}
                            className={`${classes.listBtn} ${gridDisplay ? '' : classes.active}`}>
                            {t('list')}
                            <img src={listBtnIcon} alt="" />
                        </Button>
                        <Button
                            aria-pressed={gridDisplay}
                            onClick={() => {
                                handleDisplayChange(true);
                            }}
                            className={`${classes.gridBtn} ${gridDisplay ? classes.active : ''}`}>
                            {t('grid')}
                            <img src={gridBtnIcon} alt="" />
                        </Button>
                    </ButtonGroup>
                </div>
            </div>

            {/* tabs */}
            <Tabs className={classes.tabs} value={selectedTab} onChange={handleTabChange} textColor="primary">
                {tabs.map((tab, index) => (
                    <Tab
                        disableFocusRipple
                        id={`tab-${tab}-${index}`}
                        key={`tab-${index}`}
                        label={t(tab + '.tab.title')}
                        style={{ textTransform: 'uppercase' }}
                        aria-selected={selectedTab === index ? 'true' : 'false'}
                        role="tab"
                        aria-controls={`grid-myApplications-${tab}`}
                    />
                ))}
            </Tabs>
            {/* list or grid data display */}

            <React.Fragment>
                <div className={classes.filtersGrid}>
                    <ApplicationFilters
                        applicationType={tabs[selectedTab]}
                        filtersValues={filtersValues}
                        setFiltersValues={setFiltersValues}
                        clearFilters={clearFilters}
                        exportApplications={exportApplications}
                        filterItems={filterItems}
                        datesOptions={'dateIntervalApplication'}
                        isTablet={isTablet}
                        hideInfo={hideInfo}
                        toggleInfoMessage={toggleInfoMessage}
                        handleDateFilter={handleDateFilter}
                        expoTypes={['pdf', 'excel']}
                        exportError={exportError}
                        exportLoading={exportLoading}
                        noDataToExport={noDataToExport}
                        page={'applications'}
                    />
                    {errorHandler.message !== '' && (
                        <ErrorHandler
                            title={errorHandler.message}
                            subtitle={errorHandler.subtitle}
                            action={
                                errorHandler.hasRefresh && (
                                    <IconButton
                                        disableFocusRipple
                                        className={classes.refreshButton}
                                        onClick={() => {
                                            requestApplications();
                                        }}>
                                        {t('refresh.button.text')}
                                        <RefreshIcon style={{ paddingLeft: '6.68px' }} aria-hidden="true" focusable="false" />
                                    </IconButton>
                                )
                            }
                        />
                    )}
                    {!!content &&
                        !!content.length &&
                        applicationsList &&
                        applicationsList.data &&
                        !!applicationsList.data.length &&
                        !loading &&
                        errorHandler.message === '' && (
                            <SmallPagination
                                defaultPageSize={DEFAULT_PAGE_SIZE}
                                totalElements={totalElements}
                                component={'div'}
                                count={totalElements || 0}
                                size={size || DEFAULT_PAGE_SIZE}
                                page={number || DEFAULT_PAGE_NUMBER}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                selectProps={{ renderValue: (value) => `${value} ${t('items')}` }}
                                paginationId={'myApplications'}
                            />
                        )}
                </div>
                {tabs.length > 0 && gridDisplay ? (
                    !loading ? (
                        errorHandler.message === '' && (
                            <ApplicationsGrid
                                applications={applicationsGrid}
                                gridId={`myApplications-${tabs[selectedTab]}`}
                                ariaLabelledby={`tab-${tabs[selectedTab]}-${selectedTab}`}
                            />
                        )
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress aria-hidden="true" />
                            <Typography variant="srOnly" role="alert">
                                {t('loading.text')}
                            </Typography>
                        </Box>
                    )
                ) : (
                    <div
                        className={classes.cardTable}
                        id={`grid-myApplications-${tabs[selectedTab]}`}
                        role="tabpanel"
                        aria-labelledby={`tab-${tabs[selectedTab]}-${selectedTab}`}>
                        <Card canEdit={false}>{errorHandler.message === '' && applicationTable}</Card>
                    </div>
                )}
            </React.Fragment>
            {/* pagination */}
            {!!content &&
                !!content.length &&
                !loading &&
                applicationsList &&
                applicationsList.data &&
                !!applicationsList.data.length &&
                errorHandler.message === '' && (
                    <CustomPagination
                        content={content}
                        defaultPageSize={DEFAULT_PAGE_SIZE}
                        totalElements={totalElements}
                        component={'div'}
                        count={totalElements || 0}
                        size={size || DEFAULT_PAGE_SIZE}
                        page={number || DEFAULT_PAGE_NUMBER}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        selectProps={{ renderValue: (value) => `${value} ${t('items')}` }}
                        paginationId={'myApplications'}
                        isTablet={isTablet}
                    />
                )}
            {/* error dialog */}
            {errorDialog.visibility && (
                <WarningModal
                    visibility={errorDialog.visibility}
                    message={errorDialog.message}
                    onConfirmation={handleCloseDialog}
                    confirmButtonText={t('export.unavailable.button')}
                    title={errorDialog.title}
                />
            )}
            {filterDialog.visibility && (
                <WarningModal
                    visibility={filterDialog.visibility}
                    message={t(filterDialog.message)}
                    onConfirmation={handleCancelFilterDialog}
                    confirmButtonText={t('cancel')}
                    cancelButtonText={t('continue')}
                    onCancel={handleCloseFilterDialog}
                />
            )}
        </div>
    );
};

export default MyApplicationsPage;
